// DTOs de "Línea en el tiempo" — agregación temporal de UNA línea (coordinador).
//
// Endpoint:
//   GET /api/coordinador/linea/:lineaId/tiempo?desde&hasta&granularidad&comparar
//
// Agrega TODO para una línea sobre un período (misma ventana para todas las
// secciones): KPIs, semáforo, diagnóstico (paro vs ritmo), serie temporal
// discriminada por eje (día vs hora), desglose por SKU y paros (timeline +
// pareto). `comparar=true` agrega el bloque del período anterior simétrico.
import { z } from 'zod';
import { estadoSemaforoSchema } from './supervisor_dashboard.js';
import { unidadMedidaSchema } from './unidad_medida.js';
// =============================================================================
// Peso y sobrellenado (opcional, ingesta de máquina — propuesta
// 2026-10-alimentos-rionegro, fase B)
// =============================================================================
//
// Solo aparecen cuando la línea tiene al menos un registro válido con
// cantidad_kg en el período; si no, se omiten y la respuesta queda idéntica a
// la histórica. Cuando aparecen, null = no calculable en esa fila (sin kg, sin
// unidades o SKU sin peso nominal). En `kpis` y `por_sku` son promedios
// ponderados por unidades sobre las filas con dato; sobrellenado_kg es la
// suma con signo (negativo = bajo peso).
export const pesoLineaTiempoSchema = z.object({
    gramos_unidad_real: z.number().nullable().optional(),
    peso_nominal_g: z.number().nullable().optional(),
    sobrellenado_g_unidad: z.number().nullable().optional(),
    sobrellenado_kg: z.number().nullable().optional(),
});
// =============================================================================
// Query params
// =============================================================================
export const granularidadLineaTiempoSchema = z.enum(['hora', 'dia', 'semana', 'mes']);
const boolFromQuery = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional();
export const coordinadorLineaTiempoQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    granularidad: granularidadLineaTiempoSchema.default('dia'),
    comparar: boolFromQuery,
});
// =============================================================================
// Recursos
// =============================================================================
export const lineaTiempoLineaSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    area_nombre: z.string(),
    planta_nombre: z.string(),
});
export const lineaTiempoPeriodoSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    granularidad: granularidadLineaTiempoSchema,
});
// estado_semaforo reutiliza estadoSemaforoSchema de supervisor_dashboard
// (mismo enum: verde | amarillo | rojo | sin_data).
// =============================================================================
// KPIs del período
// =============================================================================
// El programa NO se mide a nivel línea (WS-4): vive por área. La línea se mide
// solo vs CAPACIDAD (cumpl_meta_pct).
export const lineaTiempoKpisSchema = z.object({
    cumpl_meta_pct: z.number().nullable(),
    producido: z.number(),
    meta: z.number(),
    paro_min: z.number(),
    paros_count: z.number().int().min(0),
    /** bloques_capturados − paro_min/60 */
    horas_efectivas: z.number(),
    /** Promedio ponderado de golpes_por_minuto de bloques válidos donde aplica. */
    golpes_real: z.number().nullable(),
    /** Estándar representativo (skus_lineas) del SKU de mayor uso. */
    golpes_estandar: z.number().nullable(),
    golpes_aplica: z.boolean(),
    gap_total: z.number(),
    gap_paro: z.number(),
    gap_ritmo: z.number(),
    /** Unidad de producido/meta/gap: 'kg' si todo lo que corrió es GRANEL; si no 'paq'. */
    unidad: unidadMedidaSchema.optional(),
    ...pesoLineaTiempoSchema.shape,
});
// =============================================================================
// Comparativo (período anterior simétrico)
// =============================================================================
export const lineaTiempoComparativoSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    kpis: lineaTiempoKpisSchema,
    deltas: z.object({
        cumpl_meta_pct: z.number().nullable(),
        producido: z.number(),
        meta: z.number(),
        paro_min: z.number(),
        paros_count: z.number(),
    }),
});
// =============================================================================
// Diagnóstico (paro vs ritmo + culpables)
// =============================================================================
export const lineaTiempoDiagnosticoSchema = z.object({
    pct_por_paro: z.number(),
    pct_por_ritmo: z.number(),
    causal_top: z.object({ nombre: z.string(), minutos: z.number() }).nullable(),
    sku_top: z.object({ codigo: z.string(), pct_aporte_gap: z.number() }).nullable(),
});
// =============================================================================
// Serie temporal — discriminada por eje
// =============================================================================
/** Eje 'dia' (granularidad semana | mes): un punto por día agregado. */
export const lineaTiempoSerieDiaSchema = z.object({
    /** YYYY-MM-DD */
    periodo: z.string(),
    producido: z.number(),
    meta: z.number(),
    cumpl_meta_pct: z.number().nullable(),
    paro_min: z.number(),
    top_causal_nombre: z.string().nullable(),
});
/** Eje 'hora' (granularidad hora | dia): un punto por bloque-hora capturado. */
export const lineaTiempoSerieHoraSchema = z.object({
    /** HH:MM o 0-23 (string). */
    hora: z.string(),
    turno_numero: z.number().int().nullable(),
    sku_codigo: z.string().nullable(),
    producido: z.number(),
    meta_h: z.number(),
    cumpl_meta_pct: z.number().nullable(),
    operarios: z.number().int().nullable(),
    golpes_min: z.number().nullable(),
    paro_min: z.number(),
    causal_nombre: z.string().nullable(),
    validado: z.boolean(),
    estado: z.enum(['valido', 'invalidado']),
    /** Identidad del registro — para abrir el drill-down de corrección. */
    registro_id: z.string().uuid(),
    /** YYYY-MM-DD del registro. */
    fecha: z.string(),
    /** Turno del registro (nullable si el registro no tiene turno asignado). */
    turno_id: z.string().uuid().nullable(),
    /** TRUE si el registro fue creado por alta retroactiva (corrección). */
    origen_correccion: z.boolean(),
    /** Timestamp de la última corrección (nullable). */
    corregido_at: z.string().nullable(),
    /** Motivo de la corrección (nullable). */
    razon_correccion: z.string().nullable(),
    /** Unidad del bloque según el formato de su SKU. */
    unidad: unidadMedidaSchema.optional(),
    ...pesoLineaTiempoSchema.shape,
});
// =============================================================================
// Por SKU
// =============================================================================
export const lineaTiempoPorSkuSchema = z.object({
    sku_id: z.string().uuid(),
    sku_codigo: z.string(),
    sku_descripcion: z.string(),
    producido: z.number(),
    cumpl_meta_pct: z.number().nullable(),
    golpes_real: z.number().nullable(),
    golpes_estandar: z.number().nullable(),
    golpes_aplica: z.boolean(),
    horas_corridas: z.number(),
    corrio: z.boolean(),
    /** Unidad del SKU según su formato. */
    unidad: unidadMedidaSchema.optional(),
    ...pesoLineaTiempoSchema.shape,
});
// =============================================================================
// Paros (timeline + pareto)
// =============================================================================
export const lineaTiempoParoTimelineSchema = z.object({
    /** ISO timestamp. */
    hora_inicio: z.string(),
    minutos: z.number(),
    causal_madre_nombre: z.string(),
    causal_nombre: z.string(),
    /** Color hex de la categoría (del catálogo) para el punto del timeline. */
    categoria_color: z.string().nullable(),
});
export const lineaTiempoParoParetoSchema = z.object({
    causal_madre_nombre: z.string(),
    minutos: z.number(),
    paros_count: z.number().int().min(0),
    /** Color hex de la categoría (del catálogo) para la barra del Pareto. */
    categoria_color: z.string().nullable(),
});
export const lineaTiempoParosSchema = z.object({
    timeline: z.array(lineaTiempoParoTimelineSchema),
    pareto: z.array(lineaTiempoParoParetoSchema),
});
// =============================================================================
// Personal — líderes/operarios que operaron la línea en el período
// =============================================================================
export const personalLineaTiempoSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    producido: z.number(),
    cumpl_meta_pct: z.number().nullable(),
    bloques_capturados: z.number().int().min(0),
    validados: z.number().int().min(0),
    total_registros: z.number().int().min(0),
});
// =============================================================================
// Response GET /api/coordinador/linea/:lineaId/tiempo
// =============================================================================
export const coordinadorLineaTiempoSchema = z.object({
    linea: lineaTiempoLineaSchema,
    periodo: lineaTiempoPeriodoSchema,
    estado_semaforo: estadoSemaforoSchema,
    kpis: lineaTiempoKpisSchema,
    comparativo: lineaTiempoComparativoSchema.nullable(),
    diagnostico: lineaTiempoDiagnosticoSchema,
    /** 'dia' → serie_dia llena, serie_hora []. 'hora' → serie_hora llena, serie_dia []. */
    eje: z.enum(['dia', 'hora']),
    serie_dia: z.array(lineaTiempoSerieDiaSchema),
    serie_hora: z.array(lineaTiempoSerieHoraSchema),
    por_sku: z.array(lineaTiempoPorSkuSchema),
    paros: lineaTiempoParosSchema,
    /** Líderes que operaron la línea en el período (mismo desde/hasta). */
    personal: z.array(personalLineaTiempoSchema),
});
//# sourceMappingURL=coordinador_linea_tiempo.js.map